import { useState, useEffect } from 'react';

/**
 * Custom hook that tracks whether a CSS media query matches
 * @param {string} query - CSS media query string (e.g., '(max-width: 639px)')
 * @returns {boolean} - Whether the media query currently matches
 */
export function useMediaQuery(query) {
  // Default to false for SSR/initial render
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Handle SSR - window not available
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);
    
    // Set initial value
    setMatches(mediaQueryList.matches);

    const syncMatches = () => {
      setMatches(mediaQueryList.matches);
    };

    // Create event handler
    const handleChange = (event) => {
      setMatches(event.matches);
    };

    // Modern browsers use addEventListener
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(handleChange);
    }

    // Programmatic resize (devtools) may not emit change on all engines
    window.addEventListener('resize', syncMatches);

    // Cleanup
    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleChange);
      } else {
        mediaQueryList.removeListener(handleChange);
      }
      window.removeEventListener('resize', syncMatches);
    };
  }, [query]);

  return matches;
}

export default useMediaQuery;
